import React, { createContext, useContext, useState } from "react";
import {
  createStore,
  useStore as useZustandStore,
  StoreApi,
  StateCreator,
} from "zustand";

export interface CreateContextStoreV2Return<S extends object> {
  Provider: React.FC<React.PropsWithChildren<{ value?: Partial<S> }>>;
  useStore: {
    (): S;
    <T>(selector: (state: S) => T): T;
  };
  useStoreOrUndefined: {
    (): S | undefined;
    <T>(selector: (state: S) => T): T | undefined;
  };
}

const UndefinedZustandStore: StoreApi<undefined> = createStore(() => undefined);

/**
 * Creates a Zustand store and provides it through a React Context.
 *
 * @param storeCreator A function that creates a Zustand store.
 *   This function receives `set`, `get`, and `api` from Zustand.
 * @returns An object containing:
 *   - Provider: A React component to provide the store.
 *   - useStore: A hook to access the entire store or a subset using a selector.
 *   - useStoreOrUndefined: A hook to access the entire store or a subset using a selector, returning undefined if not in a Provider.
 */
export const createContextStoreV2 = <S extends object>(
  storeCreator: StateCreator<S, [], [], S>
): CreateContextStoreV2Return<S> => {
  const StoreContext = createContext<StoreApi<S> | undefined>(undefined);

  const Provider: React.FC<React.PropsWithChildren<{ value?: Partial<S> }>> = ({
    children,
    value,
  }) => {
    const [store] = useState(() => {
      if (value) {
        return createStore((set, get, api) => {
          const result = storeCreator(set, get as () => S, api as StoreApi<S>);
          return { ...result, ...value } as S;
        });
      }
      return createStore(storeCreator);
    });

    return (
      <StoreContext.Provider value={store as StoreApi<S>}>
        {children}
      </StoreContext.Provider>
    );
  };

  function useStore(): S;
  function useStore<T>(selector: (state: S) => T): T;
  function useStore<T>(selector?: (state: S) => T): S | T {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error(
        "useStore must be used within a matching Provider from createContextStoreV2"
      );
    }

    return useZustandStore(store, selector as any);
  }

  function useStoreOrUndefined(): S | undefined;
  function useStoreOrUndefined<T>(selector: (state: S) => T): T | undefined;
  function useStoreOrUndefined<T>(
    selector?: (state: S) => T
  ): S | T | undefined {
    const store = useContext(StoreContext);

    const dynamicSelector = (state: S | undefined) => {
      if (state === undefined) {
        return undefined;
      }
      return selector ? selector(state) : state;
    };

    return useZustandStore(store ?? UndefinedZustandStore, dynamicSelector);
  }

  return {
    Provider,
    useStore,
    useStoreOrUndefined,
  };
};
